import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { environment } from '../../../../environments/environment';
import { TokenService } from '../../../core/services/token.service';
import { AuthFacade } from '../../../core/services/auth.facade';
import { ThemeService, ThemeMode } from '../../../core/services/theme.service';
import { NAVIGATION, NavSection } from '../../../core/layout/navigation';
import { AppAvatarComponent } from '../app-avatar/app-avatar.component';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    MatIconModule,
    MatTooltipModule,
    MatMenuModule,
    AppAvatarComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app-sidebar.component.html',
  host: { class: 'flex h-full flex-col' },
})
export class AppSidebarComponent {
  protected readonly tokenService = inject(TokenService);
  protected readonly themeService = inject(ThemeService);
  private readonly authFacade = inject(AuthFacade);
  private readonly router = inject(Router);

  readonly sections = input<NavSection[]>(NAVIGATION);
  readonly collapsed = input(false);
  readonly collapsedChange = output<boolean>();
  /** Muestra el botón de cerrar en la cabecera (off-canvas < lg). */
  readonly isMobile = input(false);
  readonly closeRequested = output<void>();

  /** Secciones filtradas por permiso del usuario y por entorno (devOnly). */
  protected readonly visibleSections = computed<NavSection[]>(() => {
    const isDev = !environment.production;
    return this.sections()
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (item.devOnly && !isDev) return false;
          if (item.permission && !this.tokenService.hasPermission(item.permission)) return false;
          return true;
        }),
      }))
      .filter((section) => section.items.length > 0);
  });

  protected readonly themeModeLabels: Record<ThemeMode, string> = {
    light: 'Claro',
    dark: 'Oscuro',
    auto: 'Automático',
  };

  toggleCollapsed(): void {
    this.collapsedChange.emit(!this.collapsed());
  }

  setThemeMode(mode: ThemeMode): void {
    this.themeService.setMode(mode);
  }

  logout(): void {
    this.authFacade.logout().subscribe(() => void this.router.navigateByUrl('/login'));
  }
}
